// Zeitzonen-Hilfen auf Basis von Intl (ohne Temporal-Polyfill, Pflichtenheft 7.3 Abweichung dokumentiert).
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const formatters = new Map();
function formatter(timeZone) {
    let f = formatters.get(timeZone);
    if (!f) {
        f = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        formatters.set(timeZone, f);
    }
    return f;
}
export function isValidTimeZone(timeZone) {
    try {
        formatter(timeZone);
        return true;
    }
    catch {
        return false;
    }
}
function toMs(at) {
    return typeof at === 'number' ? at : at.getTime();
}
/** Wochentag 0 = Montag … 6 = Sonntag eines Kalenderdatums. */
export function weekdayOf(d) {
    const js = new Date(Date.UTC(d.year, d.month - 1, d.day)).getUTCDay(); // 0 = Sonntag
    return (js + 6) % 7;
}
export function toLocal(at, timeZone) {
    const ms = toMs(at);
    const parts = formatter(timeZone).formatToParts(new Date(ms));
    const get = (t) => Number(parts.find((p) => p.type === t)?.value ?? NaN);
    let hour = get('hour');
    if (hour === 24)
        hour = 0; // ältere ICU-Versionen
    const d = { year: get('year'), month: get('month'), day: get('day') };
    return { ...d, hour, minute: get('minute'), second: get('second'), weekday: weekdayOf(d) };
}
/** Versatz der Zeitzone zu UTC in Millisekunden zum Zeitpunkt `at` (z. B. +3 600 000 für MEZ). */
export function zoneOffsetMs(at, timeZone) {
    const ms = Math.floor(toMs(at) / 1000) * 1000;
    const l = toLocal(ms, timeZone);
    return Date.UTC(l.year, l.month - 1, l.day, l.hour, l.minute, l.second) - ms;
}
function sameWallClock(a, d, secs) {
    return (a.year === d.year && a.month === d.month && a.day === d.day && a.hour * 3600 + a.minute * 60 + a.second === secs);
}
/**
 * Lokale Wanduhrzeit → Zeitpunkt (ms seit Epoche).
 * - Doppelte Stunde (Herbst): früherer Zeitpunkt.
 * - Übersprungene Stunde (Frühling): nächste existierende Minute (Pflichtenheft 7.2).
 */
export function fromLocal(date, secondsOfDay, timeZone) {
    return Math.min(...fromLocalAll(date, secondsOfDay, timeZone));
}
/** Wie fromLocal, liefert in der doppelten Stunde aber beide Zeitpunkte (aufsteigend). */
export function fromLocalAll(date, secondsOfDay, timeZone) {
    let d = date;
    let secs = secondsOfDay;
    for (let i = 0; i < 24 * 60 + 1; i++) {
        const guess = Date.UTC(d.year, d.month - 1, d.day) + secs * 1000;
        const offsets = new Set([
            zoneOffsetMs(guess, timeZone),
            zoneOffsetMs(guess - 864e5 / 2, timeZone),
            zoneOffsetMs(guess + 864e5 / 2, timeZone),
        ]);
        const hits = [...offsets].map((o) => guess - o).filter((t) => sameWallClock(toLocal(t, timeZone), d, secs));
        if (hits.length)
            return [...new Set(hits)].sort((a, b) => a - b);
        // Lücke: zur nächsten vollen Minute weiterschalten.
        secs = (Math.floor(secs / 60) + 1) * 60;
        if (secs >= 86400) {
            secs -= 86400;
            d = addDays(d, 1);
        }
    }
    throw new Error(`Lokale Zeit nicht auflösbar in ${timeZone}`);
}
export function addDays(d, n) {
    const t = new Date(Date.UTC(d.year, d.month - 1, d.day + n));
    return { year: t.getUTCFullYear(), month: t.getUTCMonth() + 1, day: t.getUTCDate() };
}
export function formatDate(d) {
    return `${String(d.year).padStart(4, '0')}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
}
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
export function parseDate(s) {
    const m = DATE_RE.exec(s);
    if (!m)
        return null;
    const d = { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
    return formatDate(addDays(d, 0)) === s ? d : null;
}
const TIME_RE = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;
/** "HH:mm" oder "HH:mm:ss" → Sekunden seit Mitternacht; "24:00" = 86400. */
export function parseTime(s) {
    const m = TIME_RE.exec(s);
    if (!m)
        return null;
    const h = Number(m[1]);
    const min = Number(m[2]);
    const sec = Number(m[3] ?? 0);
    if (h === 24 && min === 0 && sec === 0)
        return 86400;
    if (h > 23 || min > 59 || sec > 59)
        return null;
    return h * 3600 + min * 60 + sec;
}
/** Tage zwischen zwei Kalenderdaten (b − a). */
export function daysBetween(a, b) {
    return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 864e5);
}
/** Bitmaske aus schedule_rules.weekdays (Mo = 1 … So = 64). */
export function weekdaysFromMask(mask) {
    return WEEKDAYS.filter((_, i) => (mask & (1 << i)) !== 0);
}
export function weekdaysToMask(days) {
    return days.reduce((m, d) => m | (1 << WEEKDAYS.indexOf(d)), 0);
}
//# sourceMappingURL=time.js.map